// SPDX-License-Identifier: GPL-3.0-or-later
// Reader-owned, lazy complete-book export orchestration. No PDF engine or full-result cache lives here.
import type { PdfjsViewer } from "@unilarva/pdfjs-viewer";
import type { MultikeyIndex, MultikeyRange } from "./multikey-index";
import {
  multikeyExportBatches,
  validateMultikeyExportSnapshot,
  type MultikeyExportSnapshot,
} from "./multikey-export-snapshot";
import { MultikeyExportWorkerClient } from "./multikey-export-worker-client";
import type { MultikeyWorkerClientOptions } from "./multikey-worker-client";
import { MULTIKEY_MAX_EXPORT_BYTES } from "./multikey-export-protocol";

/** Narrow processing port; tests need neither DOM nor a browser Worker. */
export type MultikeyExportClient = Pick<
  MultikeyExportWorkerClient,
  "begin" | "append" | "finish" | "dispose"
>;

/** An initialized edition's verified resource port. The session retains its inventory/edition lease. */
export interface MultikeyBookExporterOptions {
  readonly viewer: Pick<PdfjsViewer, "documentId" | "state" | "getDocumentData">;
  readonly index: MultikeyIndex;
  readonly indexIdentity: string;
  readonly signal: AbortSignal;
  readonly loadRange: (range: MultikeyRange, signal: AbortSignal) => Promise<Blob>;
  /** May use the existing cachedMultikeyWorker helper to own an offline script Blob URL. */
  readonly workerUrl?: string | URL;
  readonly workerFactory?: MultikeyWorkerClientOptions["workerFactory"];
  readonly createClient?: (options: MultikeyWorkerClientOptions) => MultikeyExportClient;
}

// Complete serialization is expensive and non-streaming. Reject overlapping exports rather than
// retaining a queue of canonical buffers/editions across readers. Display conversion is independent.
let activeExport: object | undefined;
const cancelled = (): Error => new DOMException("Multikey export cancelled", "AbortError");

/** Observes late failures while allowing abort to release admission even if an external read stalls. */
function abortable<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const abort = (): void => reject(cancelled());
    signal.addEventListener("abort", abort, { once: true });
    work.then(resolve, reject).finally(() => signal.removeEventListener("abort", abort));
    if (signal.aborted) abort();
  });
}

/** One reader identity, with at most one export worker active application-wide. Not wired to UI yet. */
export class MultikeyBookExporter {
  private readonly documentId: number;
  private readonly edition: string;
  private readonly lifetime = new AbortController();

  /** Captures only document/edition metadata. No bytes, worker or composition are admitted here. */
  public constructor(private readonly options: MultikeyBookExporterOptions) {
    const token = options.viewer.documentId;
    if (token === null || options.signal.aborted || options.viewer.state.status !== "ready")
      throw cancelled();
    this.documentId = token;
    this.edition = JSON.stringify(options.index);
  }

  private check(signal: AbortSignal): void {
    if (
      signal.aborted ||
      this.options.viewer.documentId !== this.documentId ||
      this.options.viewer.state.status !== "ready"
    )
      throw cancelled();
  }

  /** Resolves a previously admitted vector from the exact loaded canonical bytes and verified ranges.
   * No artifact-cache hit is required: deterministic regeneration handles offline cache eviction.
   * The caller owns the returned bytes; no persistent or in-memory full-book result is retained.
   */
  public async export(
    snapshot: MultikeyExportSnapshot,
    options: { signal?: AbortSignal } = {},
  ): Promise<Uint8Array> {
    const signal = AbortSignal.any([
      this.options.signal,
      this.lifetime.signal,
      ...(options.signal ? [options.signal] : []),
    ]);
    this.check(signal);
    const captured = validateMultikeyExportSnapshot(snapshot);
    if (
      captured.indexIdentity !== this.options.indexIdentity ||
      JSON.stringify(captured.index) !== this.edition
    )
      throw new Error("Multikey export edition mismatch");
    if (activeExport) throw new Error("A multikey export is already in progress");
    const owner = {};
    activeExport = owner;
    let client: MultikeyExportClient | undefined;
    try {
      client = (this.options.createClient ?? MultikeyExportWorkerClient.create)({
        signal,
        workerUrl: this.options.workerUrl,
        workerFactory: this.options.workerFactory,
      });
      // This is the public viewer's original-byte API, never a refetch of the configured URL.
      await (async () => {
        const canonical = await abortable(
          this.options.viewer.getDocumentData({ document: "original" }),
          signal,
        );
        this.check(signal);
        if (!canonical.ok) throw new Error(`Canonical PDF unavailable: ${canonical.reason}`);
        if (
          canonical.data.byteLength !== captured.index.canonical.byteLength ||
          canonical.data.byteLength > MULTIKEY_MAX_EXPORT_BYTES
        )
          throw new Error("Canonical PDF byte bounds");
        await client!.begin(
          new Blob([new Uint8Array(canonical.data)], { type: "application/pdf" }),
          captured,
          { signal },
        );
      })();
      this.check(signal);
      // Keep one immutable range at a time; neither all-range bytes nor viewer handles are retained.
      let previous: MultikeyRange | undefined;
      let blob: Blob | undefined;
      for (const batch of multikeyExportBatches(captured)) {
        if (batch.range !== previous) {
          blob = await abortable(this.options.loadRange(batch.range, signal), signal);
          this.check(signal);
          previous = batch.range;
        }
        const range = batch.range;
        await client.append(
          {
            blob: blob!,
            identity: range.identity,
            byteLength: range.byteLength,
            firstPage: range.firstPage,
            geometry: captured.index.geometry.slice(range.firstPage - 1, range.lastPage),
            occurrences: captured.index.occurrences.filter(o =>
              o.layerPageIndices.some(p => p + 1 >= range.firstPage && p + 1 <= range.lastPage),
            ),
          },
          batch.request,
          { signal },
        );
        this.check(signal);
      }
      const result = await client.finish({ signal });
      this.check(signal);
      if (
        !(result instanceof Uint8Array) ||
        !result.byteLength ||
        result.byteLength > MULTIKEY_MAX_EXPORT_BYTES
      )
        throw new Error("Export result byte bounds");
      return result;
    } finally {
      try {
        client?.dispose();
      } finally {
        if (activeExport === owner) activeExport = undefined;
      }
    }
  }

  /** Cancels active work and permanently revokes this reader's export authority. */
  public dispose(): void {
    this.lifetime.abort();
  }
}
